import { useSelector } from 'react-redux'
import Item from './Item'
import props from '../../../../../redux/props'
import { Concept, ModuleSizes } from '../../../../../types/Survey/Analysis/Concept'
import getSums from '../ConceptCalculation/OrganizationUnit/SurveyResults/getSums'
import { PrebuildMeetingGroesseBesprechungSubType, PrebuildMeetingGroesseCoworkingSubType } from '../../../../../types/Survey/Element'
import { ProductionDistance } from '../../../../../types/OrganizationUnit'
import { isUnitAllowedByProductionDistance } from './SummaryProductionDistance'
import { isUnitAllowedByFloor } from './SummaryFloor'
import { getStaufleacheLaufmeter } from '../../../../hooks/useAnalysisCalculation/byBueroType/helper/getAnswerValues'
import TableItem, { TableItemRow } from './TableItem'

type Props = { concept: Concept; productionDistance?: ProductionDistance; floorUUID?: string }

const AreaOverview: React.FC<Props> = ({ concept, productionDistance, floorUUID }) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const sums = getSums(
    productionDistance
      ? concept.units.sum.productionDistance[productionDistance]
      : floorUUID
      ? concept.units.sum.floor[floorUUID]
      : concept.units.sum,
  )

  return (
    <div>
      <div className="border-b border-blue font-bold">{t.survey.analysis.concept.summary.areaOverview.title}</div>
      <TableItem
        title={t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.label}
        value={sums.einzelarbeit + sums.einzelbueros}
        suffix={t.generic.squareMeterSuffix}
        doRound
        items={[
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.KLASSISCHE_ARBEIT}
            values={[{ value: sums.einzelarbeit, suffix: t.generic.squareMeterSuffix }]}
          />,
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR}
            values={[{ value: sums.source.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.practicalQM, suffix: t.generic.squareMeterSuffix }]}
          />,
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.einzelbueros}
            values={[{ value: sums.einzelbueros, suffix: t.generic.squareMeterSuffix }]}
          />,
        ]}
      />
      <TableItem
        title={t.survey.analysis.concept.summary.areaOverview.arbeitsorte.label}
        value={sums.kommunikation + sums.kollaboration + sums.source.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.practicalQM}
        suffix={t.generic.squareMeterSuffix}
        doRound
        items={[
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR}
            values={[{ value: sums.source.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.practicalQM, suffix: t.generic.squareMeterSuffix }]}
          />,
          ...Object.keys(PrebuildMeetingGroesseBesprechungSubType).map((key) => {
            return (
              <TableItemRow
                key={key}
                title={t.survey.analysis.concept.summary.areaOverview.arbeitsorte[`KOMMUNIKATION_${key}`]}
                values={[{ value: sums.source.MEETING_GROESSE_BESPRECHUNG[key].practicalQM, suffix: t.generic.squareMeterSuffix }]}
              />
            )
          }),
          ...Object.keys(PrebuildMeetingGroesseCoworkingSubType).map((key) => {
            return (
              <TableItemRow
                key={key}
                title={t.survey.analysis.concept.summary.areaOverview.arbeitsorte[`KOLLABORATION_${key}`]}
                values={[{ value: sums.source.MEETING_GROESSE_COWORKING[key].practicalQM, suffix: t.generic.squareMeterSuffix }]}
              />
            )
          }),
          ...Object.keys(ModuleSizes).map((size) => {
            return (
              <TableItemRow
                key={size}
                title={t.survey.analysis.concept.summary.areaOverview.arbeitsorte[`HYBRID_PERSON_${size}`]}
                values={[{ value: sums.source.HYBRID[size].practicalQM, suffix: t.generic.squareMeterSuffix }]}
              />
            )
          }),
        ]}
      />
      {!productionDistance && (
        <TableItem
          title={t.survey.analysis.concept.summary.areaOverview.uebergeordneteBedarfe.label}
          value={sums.uebergeordneteBedarfe}
          suffix={t.generic.squareMeterSuffix}
          doRound
          items={sums.source.UEBERGEORDNETE_BEDARFE.map((ue) => {
            return <TableItemRow key={ue.name} title={ue.name} values={[{ value: ue.practicalQM, suffix: t.generic.squareMeterSuffix }]} />
          })}
        />
      )}

      <TableItem
        title={t.survey.analysis.concept.summary.areaOverview.sonderbedarfe.label}
        value={sums.sonderbedarfe}
        suffix={t.generic.squareMeterSuffix}
        doRound
        items={[
          ...Object.keys(concept.units)
            .filter((u) => u !== 'sum')
            .filter((u) => isUnitAllowedByProductionDistance(concept, u, productionDistance))
            .filter((u) => isUnitAllowedByFloor(concept, u, floorUUID))
            .map((uuid) => {
              const unit = concept.units[uuid]
              return unit.ergebnisse.SONDERBEDARFE.map((s) => (
                <TableItemRow
                  key={s.name}
                  title={`${t.survey.analysis.concept.summary.areaOverview.uebergeordneteBedarfe.oePrefix}${unit.name}`}
                  values={[{ value: s.name }, { value: s.practicalQM, suffix: t.generic.squareMeterSuffix }]}
                />
              ))
            }),
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.sonderbedarfe.locker}
            values={[{ value: '' }, { value: sums.locker, suffix: t.generic.squareMeterSuffix }]}
          />,
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.sonderbedarfe.drucker}
            values={[{ value: '' }, { value: sums.drucker, suffix: t.generic.squareMeterSuffix }]}
          />,
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.sonderbedarfe.workcafe}
            values={[{ value: '' }, { value: sums.workcafe, suffix: t.generic.squareMeterSuffix }]}
          />,
          <TableItemRow
            title={t.survey.analysis.concept.summary.areaOverview.sonderbedarfe.garderobe}
            values={[{ value: '' }, { value: sums.garderobe, suffix: t.generic.squareMeterSuffix }]}
          />,
        ]}
      />
      <TableItem
        title={t.survey.analysis.concept.summary.areaOverview.stauraum}
        value={sums.staufleache}
        suffix={t.generic.squareMeterSuffix}
        doRound
        items={Object.keys(concept.units)
          .filter((u) => u !== 'sum')
          .filter((u) => isUnitAllowedByProductionDistance(concept, u, productionDistance))
          .filter((u) => isUnitAllowedByFloor(concept, u, floorUUID))
          .map((uuid) => {
            const unit = concept.units[uuid]
            return (
              <TableItemRow
                key={uuid}
                title={unit.name}
                values={[
                  { value: unit.ergebnisse.STAUFLAECHE.practicalQM, suffix: t.generic.squareMeterSuffix },
                  { value: getStaufleacheLaufmeter(unit.ergebnisse.STAUFLAECHE.practicalQM), suffix: t.generic.laufmeterSuffix },
                ]}
              />
            )
          })}
      />
      <TableItem
        title={t.survey.analysis.concept.summary.areaOverview.verkehrsflaechen.label}
        value={sums.verkehrsflaechen}
        suffix={t.generic.squareMeterSuffix}
        doRound
        items={Object.keys(concept.units)
          .filter((u) => u !== 'sum')
          .filter((u) => isUnitAllowedByProductionDistance(concept, u, productionDistance))
          .filter((u) => isUnitAllowedByFloor(concept, u, floorUUID))
          .map((uuid) => {
            const unit = concept.units[uuid]
            return (
              <TableItemRow
                key={uuid}
                title={unit.name}
                values={[{ value: unit.ergebnisse.verkehrsflaechen.practicalQM, suffix: t.generic.squareMeterSuffix }]}
              />
            )
          })}
      />
      <Item
        bold
        title={t.survey.analysis.concept.summary.areaOverview.total}
        value={sums.source.total}
        suffix={t.generic.squareMeterSuffix}
        doRound
      />
    </div>
  )
}

export default AreaOverview
